import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Calendar as CalendarIcon,
  Mail,
  MapPin,
  Phone,
  Send,
  AlertCircle,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------------- */
/* Constants                                                                 */
/* ------------------------------------------------------------------------- */

const WHATSAPP_NUMBER = "919979068119"; // wa.me format — digits only, no +/space
const SHEETS_WEBHOOK_URL =
  process.env.REACT_APP_SHEETS_WEBHOOK_URL ||
  "REPLACE_WITH_YOUR_GOOGLE_APPS_SCRIPT_WEB_APP_URL";

const EVENT_TYPES = [
  "Wedding",
  "Reception",
  "Engagement / Anniversary",
  "Birthday Party",
  "Festive / Garba",
  "Corporate Event",
  "Social Gathering",
  "Other",
];

const BUDGET_RANGES = [
  "Under ₹1 Lakh",
  "₹1 – 3 Lakh",
  "₹3 – 7 Lakh",
  "₹7 – 15 Lakh",
  "₹15 Lakh +",
  "Not sure yet",
];

/* ------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* ------------------------------------------------------------------------- */

// Strip control chars + trim. Used before saving / building WhatsApp text.
const sanitize = (v) =>
  String(v ?? "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()
    .slice(0, 2000);

const isValidEmail = (s) =>
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(s);

// Accepts +91 …, 91 …, 10-digit Indian, or any 7–15 digit international number.
const isValidPhone = (s) => {
  const digits = String(s).replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
};

function buildWhatsAppMessage(d) {
  const line = (label, val) => (val ? `${label}: ${val}` : null);
  const lines = [
    "*New Event Enquiry — Shree Event Planner*",
    "",
    line("Name", d.name),
    line("Phone", d.phone),
    line("Email", d.email),
    line("Event Type", d.event_type),
    line("Event Date", d.event_date),
    line("Event Location", d.event_location),
    line("Guests", d.guests),
    line("Budget Range", d.budget),
    "",
    d.message ? "*Message:*" : null,
    d.message || null,
  ].filter(Boolean);
  return lines.join("\n");
}

async function postToGoogleSheet(payload) {
  if (
    !SHEETS_WEBHOOK_URL ||
    SHEETS_WEBHOOK_URL.startsWith("REPLACE_WITH_")
  ) {
    return { ok: false, reason: "not_configured" };
  }
  try {
    // text/plain avoids preflight CORS — Apps Script accepts it.
    await axios.post(SHEETS_WEBHOOK_URL, JSON.stringify(payload), {
      headers: { "Content-Type": "text/plain;charset=utf-8" },
      timeout: 12000,
    });
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err.message || "network_error" };
  }
}

/* ------------------------------------------------------------------------- */
/* Component                                                                  */
/* ------------------------------------------------------------------------- */

const initialForm = {
  name: "",
  phone: "",
  email: "",
  event_type: "",
  event_date: undefined,
  event_location: "",
  guests: "",
  budget: "",
  message: "",
  website: "", // honeypot — must stay empty
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.phone.trim()) e.phone = "Phone number is required.";
    else if (!isValidPhone(form.phone)) e.phone = "Please enter a valid phone number.";
    if (form.email && !isValidEmail(form.email.trim()))
      e.email = "Please enter a valid email address.";
    if (!form.event_type) e.event_type = "Select an event type.";
    if (!form.event_date) e.event_date = "Select your event date.";
    if (!form.event_location.trim()) e.event_location = "Event location is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();

    // Honeypot — silently drop bots.
    if (form.website) return;

    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    setLoading(true);

    const eventDateISO = form.event_date
      ? format(form.event_date, "yyyy-MM-dd")
      : "";
    const eventDateFmt = form.event_date
      ? format(form.event_date, "EEE, d MMM yyyy")
      : "";

    const payload = {
      name: sanitize(form.name),
      phone: sanitize(form.phone),
      email: sanitize(form.email),
      event_type: sanitize(form.event_type),
      event_date: eventDateISO,
      event_location: sanitize(form.event_location),
      guests: sanitize(form.guests),
      budget: sanitize(form.budget),
      message: sanitize(form.message),
      source_page:
        typeof window !== "undefined" ? window.location.href : "",
      timestamp: new Date().toISOString(),
    };

    // 1. Save to Google Sheet (best-effort — WhatsApp still opens if it fails)
    const saved = await postToGoogleSheet(payload);

    // 2. Build WhatsApp link
    const waText = buildWhatsAppMessage({
      ...payload,
      event_date: eventDateFmt,
    });
    const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
      waText
    )}`;

    // 3. Open WhatsApp in a new tab
    window.open(waUrl, "_blank", "noopener,noreferrer");

    if (saved.ok) {
      toast.success(
        "Enquiry sent. WhatsApp is open — please tap Send to confirm."
      );
    } else {
      toast.message("Your WhatsApp enquiry is ready", {
        description: "Please tap Send in WhatsApp to complete your enquiry.",
      });
    }

    setForm(initialForm);
    setErrors({});
    setLoading(false);
  };

  /* ------------------- render ------------------- */
  return (
    <section id="contact" className="relative section-pad" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left — info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-gold" />
              <span className="text-xs uppercase tracking-[0.3em] text-gold font-medium">
                Get in Touch
              </span>
            </div>
            <h2
              className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
              data-testid="contact-heading"
            >
              Let's plan your perfect{" "}
              <span className="italic text-gold-gradient">celebration.</span>
            </h2>
            <p className="mt-6 text-ivory/65 text-base leading-relaxed font-light max-w-md">
              Tell us about your event and we'll help you plan every detail with
              care, creativity and smooth coordination.
            </p>

            <div className="mt-10 space-y-5">
              {[
                {
                  icon: Phone,
                  label: "Phone / WhatsApp",
                  value: "+91 99790 68119",
                  href: "tel:+919979068119",
                },
                {
                  icon: Mail,
                  label: "Email",
                  value: "hello@shreeeventplanner.com",
                  href: "mailto:hello@shreeeventplanner.com",
                },
                {
                  icon: MapPin,
                  label: "Studio",
                  value: "Vadodara, Gujarat · India",
                },
              ].map((c, i) => {
                const Icon = c.icon;
                const Wrap = c.href ? "a" : "div";
                return (
                  <Wrap
                    key={c.label}
                    href={c.href}
                    className="glass rounded-xl p-4 flex items-center gap-4 hover:border-gold/30 transition-colors"
                    data-testid={`contact-info-${i}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gold/30 to-gold/5 border border-gold/20 flex items-center justify-center shrink-0">
                      <Icon size={16} className="text-gold" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-ivory/45">
                        {c.label}
                      </p>
                      <p className="text-ivory text-sm md:text-base font-light">
                        {c.value}
                      </p>
                    </div>
                  </Wrap>
                );
              })}
            </div>
          </motion.div>

          {/* Right — form */}
          <motion.form
            onSubmit={onSubmit}
            noValidate
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-7 glass-gold rounded-2xl p-7 md:p-10 relative overflow-hidden"
            data-testid="contact-form"
          >
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-gold/10 blur-[100px] rounded-full pointer-events-none" />

            {/* Honeypot — invisible to humans, irresistible to bots */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-9999px",
                width: 1,
                height: 1,
                overflow: "hidden",
              }}
            >
              <label htmlFor="website-hp">Website (leave blank)</label>
              <input
                type="text"
                id="website-hp"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
              />
            </div>

            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Name" required error={errors.name}>
                <Input
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  className={fieldClass(errors.name)}
                  placeholder="Your full name"
                  autoComplete="name"
                  aria-required="true"
                  aria-invalid={!!errors.name}
                />
              </Field>

              <Field label="Phone" required error={errors.phone}>
                <Input
                  type="tel"
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className={fieldClass(errors.phone)}
                  placeholder="+91 ..."
                  autoComplete="tel"
                  aria-required="true"
                  aria-invalid={!!errors.phone}
                />
              </Field>

              <Field label="Email" error={errors.email}>
                <Input
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className={fieldClass(errors.email)}
                  placeholder="you@example.com (optional)"
                  autoComplete="email"
                  aria-invalid={!!errors.email}
                />
              </Field>

              <Field label="Event Type" required error={errors.event_type}>
                <Select value={form.event_type} onValueChange={(v) => set("event_type", v)}>
                  <SelectTrigger
                    data-testid="contact-select-event-type"
                    className={fieldClass(errors.event_type)}
                    aria-required="true"
                  >
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A1422] border-white/10 text-ivory">
                    {EVENT_TYPES.map((t) => (
                      <SelectItem
                        key={t}
                        value={t}
                        data-testid={`event-type-option-${t
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Event Date" required error={errors.event_date}>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="contact-input-date-trigger"
                      className={cn(
                        "w-full justify-start h-12 bg-white/[0.04] border-white/10 text-left font-light text-ivory hover:bg-white/[0.06] hover:text-ivory",
                        !form.event_date && "text-ivory/45",
                        errors.event_date && "border-rose-500/70"
                      )}
                      aria-required="true"
                    >
                      <CalendarIcon size={16} className="mr-2 text-gold" />
                      {form.event_date
                        ? format(form.event_date, "PPP")
                        : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto p-0 bg-[#0A1422] border-white/10"
                    align="start"
                  >
                    <Calendar
                      mode="single"
                      selected={form.event_date}
                      onSelect={(d) => set("event_date", d)}
                      initialFocus
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      className="text-ivory"
                    />
                  </PopoverContent>
                </Popover>
              </Field>

              <Field label="Event Location" required error={errors.event_location}>
                <Input
                  data-testid="contact-input-location"
                  value={form.event_location}
                  onChange={(e) => set("event_location", e.target.value)}
                  className={fieldClass(errors.event_location)}
                  placeholder="City / Venue"
                  autoComplete="off"
                  aria-required="true"
                />
              </Field>

              <Field label="Number of Guests">
                <Input
                  type="number"
                  min="1"
                  data-testid="contact-input-guests"
                  value={form.guests}
                  onChange={(e) => set("guests", e.target.value)}
                  className={fieldClass()}
                  placeholder="e.g. 250 (optional)"
                />
              </Field>

              <Field label="Budget Range">
                <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
                  <SelectTrigger
                    data-testid="contact-select-budget"
                    className={fieldClass()}
                  >
                    <SelectValue placeholder="Select a range (optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A1422] border-white/10 text-ivory">
                    {BUDGET_RANGES.map((b) => (
                      <SelectItem key={b} value={b}>
                        {b}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <div className="md:col-span-2">
                <Field label="Message">
                  <Textarea
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                    rows={4}
                    className="bg-white/[0.04] border-white/10 text-ivory placeholder:text-ivory/35 focus:border-gold/60 resize-none"
                    placeholder="Anything else we should know — vision, themes, special requests… (optional)"
                  />
                </Field>
              </div>

              <div className="md:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 pt-2">
                <p className="text-xs text-ivory/45 sm:max-w-sm leading-relaxed">
                  We'll save your enquiry and open WhatsApp with the details
                  pre-filled. Tap <strong className="text-ivory/70">Send</strong>{" "}
                  in WhatsApp to complete.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-gold inline-flex items-center justify-center gap-2 disabled:opacity-60 text-sm sm:text-base px-5 sm:px-7 py-3.5 w-full sm:w-auto text-center leading-tight"
                  data-testid="contact-submit-button"
                  aria-label="Send Enquiry on WhatsApp"
                >
                  {loading ? "Sending..." : "Send Enquiry on WhatsApp"}
                  <Send size={14} className="shrink-0" />
                </button>
              </div>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

/* ----- subcomponents ----- */
function Field({ label, required, error, children }) {
  return (
    <div>
      <Label className="text-ivory/70 text-xs uppercase tracking-[0.2em] mb-2 block">
        {label}
        {required && <span className="text-gold ml-1">*</span>}
      </Label>
      {children}
      {error && (
        <p
          className="mt-1.5 text-xs text-rose-300/85 flex items-center gap-1.5"
          data-testid="contact-error"
        >
          <AlertCircle size={12} /> {error}
        </p>
      )}
    </div>
  );
}

function fieldClass(err) {
  return cn(
    "bg-white/[0.04] border-white/10 text-ivory placeholder:text-ivory/35 focus:border-gold/60 h-12",
    err && "border-rose-500/70 focus:border-rose-400/80"
  );
}
